import React from 'react';
import {
  SiDjango, SiFastapi, SiFlask, SiExpress, SiNestjs, SiReact, SiNextdotjs,
  SiVuedotjs, SiSpringboot, SiGin, SiLaravel, SiDotnet, SiHtml5, SiOdoo,
  SiWordpress, SiJoomla, SiDrupal, SiNextcloud, SiMatomo, SiKeycloak, SiN8N,
  SiMetabase, SiHasura, SiPostgresql, SiMysql, SiRedis, SiRabbitmq,
  SiApachekafka, SiRubyonrails, SiNuxtdotjs, SiSvelte, SiRemix,
} from 'react-icons/si';
import { FaFileSignature, FaRobot, FaUniversity } from 'react-icons/fa';
import { IconType } from 'react-icons';

/**
 * Logos de marque du catalogue.
 *
 * Les icônes génériques d'Heroicons ne distinguaient pas les briques entre
 * elles : PostgreSQL et MySQL partageaient CircleStackIcon, Redis et Kafka
 * BoltIcon, et les trois frameworks Python la même CommandLineIcon. La carte
 * n'apportait donc aucune information que le titre ne donnait déjà.
 *
 * Les couleurs sont les couleurs de marque officielles (Simple Icons). Elles ne
 * sont PAS utilisables telles quelles : cinq logos sont noirs ou quasi noirs
 * (Flask, Express, Next.js, Django, Kafka) et disparaissent sur le thème
 * sombre, pendant qu'une dizaine d'autres — React, Vue, Hasura, Spring Boot —
 * n'ont pas assez de contraste sur le thème clair. Remplacer des icônes
 * illisibles par des logos illisibles n'aurait rien réglé.
 *
 * D'où `readableOn` : la teinte de marque est conservée, seule sa clarté est
 * ajustée jusqu'à franchir 3:1 face à la surface. C'est le seuil WCAG des
 * éléments non textuels. La correction est faite ICI, par construction, plutôt
 * que laissée au jugement de qui ajoutera la prochaine marque.
 */

/** Luminance relative WCAG d'une couleur `RRGGBB`. */
export function luminance(hex: string): number {
  const c = [0, 2, 4]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/** Rapport de contraste WCAG entre deux couleurs `RRGGBB`. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function toHex(h: number, s: number, l: number): string {
  const f = (n: number) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255).toString(16).padStart(2, '0');
  };
  return `${f(0)}${f(8)}${f(4)}`;
}

/**
 * La couleur de marque, éclaircie ou assombrie juste ce qu'il faut pour
 * franchir `target` face à `surface`. La teinte et la saturation sont
 * intactes : c'est toujours le rouge de Laravel, pas un rouge quelconque.
 *
 * Le sens de la correction suit la surface — on éclaircit sur fond sombre, on
 * assombrit sur fond clair — et s'arrête aux extrêmes, où la teinte n'existe
 * plus. Une marque achromatique (Kafka, quasi noir) finit donc en blanc cassé
 * sur le thème sombre, ce qui est le comportement voulu : lisible d'abord.
 */
export function readableOn(brandHex: string, surfaceHex: string, target = 3): string {
  const hex = brandHex.replace('#', '').toLowerCase();
  if (contrast(hex, surfaceHex) >= target) return `#${hex}`;
  const [h, s] = toHsl(hex);
  const lighten = luminance(surfaceHex) < 0.5;
  let best = hex;
  // Pas de 1 % : suffisamment fin pour ne pas sur-corriger, assez grossier
  // pour rester lisible en revue.
  for (let step = 1; step <= 100; step++) {
    const [, , l0] = toHsl(hex);
    const l = lighten ? Math.min(1, l0 + step / 100) : Math.max(0, l0 - step / 100);
    best = toHex(h, s, l);
    if (contrast(best, surfaceHex) >= target) break;
  }
  return `#${best}`;
}

/**
 * Les surfaces les PLUS DEFAVORABLES parmi celles qu'une carte peut prendre,
 * pas les plus courantes (cf. src/index.css).
 *
 * Une carte disponible est sur `--color-surface-2`, une carte grisee sur
 * `--color-surface-3`. En thema sombre surface-3 (#232323) est plus CLAIR que
 * surface-2 (#1B1B1B) : c'est donc lui qui laisse le moins de contraste a un
 * logo. Viser surface-2 laissait passer Matomo a 2,76 sur une carte grisee --
 * constate au navigateur, pas en test, parce que les tests visaient la meme
 * surface trop indulgente que le composant.
 *
 * En theme clair c'est l'inverse : surface-2 (#FFFFFF) est le plus clair, donc
 * le plus defavorable.
 */
export const SURFACE_LIGHT = 'ffffff';
export const SURFACE_DARK = '232323';

/**
 * Couleurs de marque officielles (Simple Icons 13.21.0), telles quelles.
 * Ne PAS les corriger à la main ici : `readableOn` s'en charge, et les tests
 * vérifient le résultat sur les deux thèmes.
 */
/**
 * `hex: null` = pas de logo de marque publie pour cette application.
 *
 * On pose alors une icone generique dans la couleur de texte du theme, plutot
 * que d'inventer une couleur de marque. Une teinte devinee est pire qu'une
 * teinte neutre : elle affirme une identite visuelle que l'editeur n'a jamais
 * choisie, et elle sera fausse le jour ou il en publiera une.
 */
export const BRANDS: Record<string, { Icon: IconType; hex: string | null }> = {
  'python-django': { Icon: SiDjango, hex: '092E20' },
  'python-fastapi': { Icon: SiFastapi, hex: '009688' },
  'python-flask': { Icon: SiFlask, hex: '000000' },
  'nodejs-express': { Icon: SiExpress, hex: '000000' },
  'nodejs-nestjs': { Icon: SiNestjs, hex: 'E0234E' },
  react: { Icon: SiReact, hex: '61DAFB' },
  nextjs: { Icon: SiNextdotjs, hex: '000000' },
  vue: { Icon: SiVuedotjs, hex: '4FC08D' },
  'java-springboot': { Icon: SiSpringboot, hex: '6DB33F' },
  'go-gin': { Icon: SiGin, hex: '008ECF' },
  'php-laravel': { Icon: SiLaravel, hex: 'FF2D20' },
  dotnet: { Icon: SiDotnet, hex: '512BD4' },
  'html-static': { Icon: SiHtml5, hex: 'E34F26' },
  odoo: { Icon: SiOdoo, hex: '714B67' },
  wordpress: { Icon: SiWordpress, hex: '21759B' },
  joomla: { Icon: SiJoomla, hex: '5091CD' },
  drupal: { Icon: SiDrupal, hex: '0678BE' },
  nextcloud: { Icon: SiNextcloud, hex: '0082C9' },
  matomo: { Icon: SiMatomo, hex: '3152A0' },
  keycloak: { Icon: SiKeycloak, hex: '4D4D4D' },
  n8n: { Icon: SiN8N, hex: 'EA4B71' },
  metabase: { Icon: SiMetabase, hex: '509EE3' },
  hasura: { Icon: SiHasura, hex: '1EB4D4' },
  postgresql: { Icon: SiPostgresql, hex: '4169E1' },
  mysql: { Icon: SiMysql, hex: '4479A1' },
  redis: { Icon: SiRedis, hex: 'FF4438' },
  rabbitmq: { Icon: SiRabbitmq, hex: 'FF6600' },
  kafka: { Icon: SiApachekafka, hex: '231F20' },
  // DocuSeal n'est pas dans Simple Icons : icone generique, couleur du theme.
  docuseal: { Icon: FaFileSignature, hex: null },
  // Open WebUI non plus : meme repli neutre que DocuSeal.
  openwebui: { Icon: FaRobot, hex: null },
  // Open Bank Project non plus : repli neutre.
  'ruby-rails': { Icon: SiRubyonrails, hex: '#D30001' },
  nuxt: { Icon: SiNuxtdotjs, hex: '#00DC82' },
  sveltekit: { Icon: SiSvelte, hex: '#FF3E00' },
  remix: { Icon: SiRemix, hex: '#000000' },
};

interface BrandLogoProps {
  /** `type` du modèle de catalogue (`postgresql`, `nodejs-express`, …). */
  type: string;
  className?: string;
}

/**
 * Les deux couleurs corrigées sont posées en variables CSS ; c'est la feuille
 * de style qui choisit laquelle s'applique (`.dark .brand-logo`). Résoudre le
 * thème en JavaScript obligerait ce composant à s'abonner au changement de
 * thème pour rien.
 */
const BrandLogo: React.FC<BrandLogoProps> = ({ type, className = 'w-8 h-8' }) => {
  const brand = BRANDS[type];
  if (!brand) return null;
  // Meme contournement que `RIcon` dans RepositoryConnectorV2 : les types de
  // react-icons 5 et ceux de React 19 ne s'accordent pas sur `ReactNode`.
  const Icon = brand.Icon as React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
    'aria-hidden'?: boolean | 'true' | 'false';
  }>;
  return (
    <Icon
      className={`brand-logo ${className}`}
      aria-hidden="true"
      style={
        brand.hex === null
          ? undefined
          : ({
              '--brand-light': readableOn(brand.hex, SURFACE_LIGHT),
              '--brand-dark': readableOn(brand.hex, SURFACE_DARK),
            } as React.CSSProperties)
      }
    />
  );
};

export default BrandLogo;
